import { useEffect, useRef } from 'react'
import { X, Phone, Users, Clock, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Booking } from '@/lib/mockBookings'

interface BookingDetailModalProps {
  booking: Booking | null
  isOpen: boolean
  onClose: () => void
  t: (key: string) => string
}

export function BookingDetailModal({
  booking,
  isOpen,
  onClose,
  t,
}: BookingDetailModalProps) {
  const scrollYRef = useRef(0)

  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollYRef.current}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      window.scrollTo(0, scrollYRef.current)
    }
    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen || !booking) return null

  const statusConfig = {
    confirmed: {
      label: t('status.confirmed'),
      dotClass: 'bg-maia',
      bgClass: 'bg-maia/10',
      textClass: 'text-maia-dark',
    },
    cancelled: {
      label: t('status.cancelled'),
      dotClass: 'bg-pizza',
      bgClass: 'bg-pizza/10',
      textClass: 'text-pizza-dark',
    },
    pending: {
      label: t('status.pending'),
      dotClass: 'bg-gray-400',
      bgClass: 'bg-gray-100',
      textClass: 'text-gray-700',
    },
  } as const

  const config = statusConfig[booking.status]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-detail-title"
    >
      <div className="w-full max-w-sm max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h2
            id="booking-detail-title"
            className="font-display text-xl font-bold text-ink"
          >
            {t('zone.bookings.detailTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('aria.close')}
            className="rounded-full p-2 text-ink/70 transition-colors hover:bg-cord hover:text-ink"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-cream text-maia-dark">
              <Clock className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted">
                {t('zone.bookings.time')}
              </p>
              <p className="font-display text-lg font-bold text-ink">
                {booking.time}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-cream text-maia-dark">
              <Users className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted">
                {t('zone.bookings.guests')}
              </p>
              <p className="font-display text-lg font-bold text-ink">
                {booking.guests}{' '}
                {booking.guests === 1
                  ? t('zone.bookings.coverSingular') ?? 'coperto'
                  : t('zone.bookings.coverPlural') ?? 'coperti'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-cream text-maia-dark">
              <Info className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted">
                {t('zone.bookings.name')}
              </p>
              <p className="font-medium text-ink">{booking.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-cream text-maia-dark">
              <Phone className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted">
                {t('zone.bookings.phone')}
              </p>
              <a
                href={`tel:${booking.phone}`}
                className="font-medium text-maia-dark hover:text-pizza hover:underline"
              >
                {booking.phone}
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-cream text-maia-dark">
              <span
                className={cn('h-3 w-3 rounded-full', config.dotClass)}
                aria-hidden="true"
              />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted">
                {t('zone.bookings.status')}
              </p>
              <p className={cn('font-medium', config.textClass)}>
                {config.label}
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-full bg-ink px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-ink/90"
        >
          {t('zone.bookings.closeDetail')}
        </button>
      </div>
    </div>
  )
}
